import { Input } from "@screencloud/screencloud-ui-components";
import { useEffect, useState } from "react";
import { UUID } from "@screencloud/uuid";

import { CreateQuickPostContainer } from "./styles";
import { FilestackUploadedFile } from "../../hooks/useCreateQuickPost";
import { getFilestackPicker } from "./utils/filestack";
import { createAppConfig, createHook } from "./utils/hookService";
import { createAppManagementToken } from "src/state/helper/query";
import { useAppContext } from "src/hooks/useAppContext";
import quickPostIcon from "src/pages/Welcome/images/quick-post-icon.png";
import { TextAreaWithRichFormatting } from "./TextAreaWithRichFormatting";
import { UploadedFiles } from "./UploadedFiles";

type Props = {
  title: string;
  body: string;
  uploadedFiles: FilestackUploadedFile[];
  appPlatformData: { appConfigId: UUID; hookId: UUID; feedId: UUID };
  setTitle: (title: string) => void;
  setBody: (body: string) => void;
  setUploadedFiles: React.Dispatch<
    React.SetStateAction<FilestackUploadedFile[]>
  >;
  setAppPlatformData: React.Dispatch<
    React.SetStateAction<{ appConfigId: UUID; hookId: UUID; feedId: UUID }>
  >;
};

const MAX_UPLOADED_FILES = 4;

export const CreateQuickPost = (props: Props) => {
  const {
    title,
    body,
    uploadedFiles,
    appPlatformData,
    setTitle,
    setBody,
    setUploadedFiles,
    setAppPlatformData,
  } = props;

  const context = useAppContext();
  const [remainingNumberOfFiles, setRemainingNumberOfFiles] =
    useState(MAX_UPLOADED_FILES);

  // Effect to initialize the picker when component mounts
  useEffect(() => {
    const initializeHookData = async () => {
      // Only create hook if hookId and feedId are both null/undefined
      if (
        !appPlatformData.appConfigId ||
        !appPlatformData.hookId ||
        !appPlatformData.feedId
      ) {
        try {
          const signedToken = await createAppManagementToken(
            context.currentSpace?.id,
          );

          if (!signedToken) {
            console.error("No authentication token available");
            return;
          }

          const appConfigId = await createAppConfig(signedToken);
          const hookDaraFromRes = await createHook(signedToken, appConfigId);
          setAppPlatformData({
            appConfigId,
            hookId: hookDaraFromRes.hookId,
            feedId: hookDaraFromRes.feedId,
          });
        } catch (error) {
          console.error("Failed to initialize hook data:", error);
        }
      }
    };

    initializeHookData();

    // Initialize the picker
    const picker = getFilestackPicker({
      remainingNumberOfFiles,
      setRemainingNumberOfFiles,
      setUploadedFiles,
    });
    picker.open();
  }, []);

  // Create a new picker whenever remainingNumberOfFiles changes
  useEffect(() => {
    // Skip the initial render - we already handle that in the first useEffect
    if (remainingNumberOfFiles >= 1) {
      const picker = getFilestackPicker({
        remainingNumberOfFiles,
        setRemainingNumberOfFiles,
        setUploadedFiles,
      });
      picker.open();
    }
  }, [remainingNumberOfFiles]);

  return (
    <CreateQuickPostContainer>
      <div className="quick-post-form">
        <div>
          <div className="quick-post-form__header">
            <img src={quickPostIcon} alt="Quick Post" />
            <h1>Quick Post</h1>
          </div>
          <p className="quick-post-form__description">
            Ideal for front desk staff, office managers, or team leads who want
            to post something without needing a designer or marketer. Look for
            Quick Post in our App Store.
          </p>
        </div>
        <div>
          <div className="input-title-container">
            <h3>Title</h3>
            <span className="field-constraints">max 128 words</span>
          </div>
          <Input
            className="title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a short title"
            maxLength={128}
          />
        </div>
        <div>
          <div className="input-title-container">
            <h3>Body</h3>
            <span />
          </div>
          <TextAreaWithRichFormatting
            value={body}
            onChange={(value) => setBody(value)}
            placeholder="Type your message"
          />
        </div>
      </div>
      <div className="file-upload">
        <b>Upload Media</b>
        <span className="field-constraints">
          You can add up to 4 images (png, jpg, gif) to this post. Maximum size
          of 10MB per image.
        </span>
        <div id="droppane" />
        <UploadedFiles
          uploadedFiles={uploadedFiles}
          remainingNumberOfFiles={remainingNumberOfFiles}
          setUploadedFiles={setUploadedFiles}
          setRemainingNumberOfFiles={setRemainingNumberOfFiles}
        />
      </div>
    </CreateQuickPostContainer>
  );
};
